/** @jsxImportSource react */
import { Badge, Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Toolbar, Skeleton, Tooltip } from '@mui/material'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ReplyAllOutlinedIcon from '@mui/icons-material/ReplyAllOutlined'
import SellOutlinedIcon from '@mui/icons-material/SellOutlined'
import { useQuery } from '@tanstack/react-query'
import { tasksAPI } from '../../api/crm'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import DynamicFormOutlinedIcon from '@mui/icons-material/DynamicFormOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectIsTenantAdmin } from '../../store/slices/tenantSlice'
import { useEntityTypes, useIsPlatformAdmin, useSubscription, useTenantKey } from '../../hooks/useCrmQueries'
import { entityLabelPlural } from '../../utils/helpers'

export const DRAWER_WIDTH = 232

const NavItem = ({ icon, label, path, exact, onNavigate, badge }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const selected = exact ? pathname === path : pathname === path || pathname.startsWith(`${path}/`)
  return (
    <ListItemButton
      selected={selected}
      onClick={() => {
        navigate(path)
        onNavigate?.()
      }}
      sx={{ mx: 1, mb: 0.25, py: 0.6 }}
    >
      <ListItemIcon sx={{ minWidth: 34 }}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
      {badge}
    </ListItemButton>
  )
}

// const SectionLabel = ({ children }) => (
//   <ListSubheader sx={{ lineHeight: '32px', mt: 1, bgcolor: 'transparent', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
//     {children}
//   </ListSubheader>
// )

const SectionLabel = ({ children }) => (
  <ListSubheader sx={{
    position: 'static',
    lineHeight: '32px',
    mt: 1,
    px: 2,
    bgcolor: 'transparent',
    fontSize: 11, 
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  }}>
    {children}
  </ListSubheader>
)
/** Overdue and due-today count of the member's own tasks (red when something is overdue). */
const TaskBadge = () => {
  const tenantId = useTenantKey()
  const { data } = useQuery({
    queryKey: [tenantId, 'tasks', 'summary'],
    queryFn: tasksAPI.summary,
    enabled: !!tenantId,
    refetchInterval: 60_000,
  })
  if (!data?.dueToday) return null
  return (
    <Tooltip title={`${data.overdue} overdue, ${data.dueToday - data.overdue} more due today`}>
      <Badge badgeContent={data.dueToday} color={data.overdue ? 'error' : 'primary'} sx={{ mr: 1 }} />
    </Tooltip>
  )
}

const SidebarContent = ({ onNavigate }) => {
  const isAdmin = useSelector(selectIsTenantAdmin)
  const isPlatformAdmin = useIsPlatformAdmin()
  const { entityTypes, isLoading } = useEntityTypes()
  const { data: subscription } = useSubscription()

  return (
    <Box sx={{ overflowY: 'auto', py: 1 }}>
      <List dense disablePadding>
        <NavItem icon={<HomeOutlinedIcon fontSize="small" />} label="Home" path="/" exact onNavigate={onNavigate} />
        <NavItem icon={<TaskAltOutlinedIcon fontSize="small" />} label="Tasks" path="/tasks" onNavigate={onNavigate} badge={<TaskBadge />} />
        <NavItem icon={<CalendarMonthOutlinedIcon fontSize="small" />} label="Calendar" path="/calendar" onNavigate={onNavigate} />
        <NavItem icon={<TableChartOutlinedIcon fontSize="small" />} label="Reports" path="/reports" onNavigate={onNavigate} />
        <NavItem icon={<TrendingUpOutlinedIcon fontSize="small" />} label="Forecast" path="/forecast" onNavigate={onNavigate} />
        <NavItem icon={<FactCheckOutlinedIcon fontSize="small" />} label="Approvals" path="/approvals" onNavigate={onNavigate} />
        <SectionLabel>Records</SectionLabel>
        {isLoading && [1, 2].map((i) => <Skeleton key={i} sx={{ mx: 2 }} height={32} />)}
        {entityTypes.map((type) => (
          <NavItem key={type} icon={<FolderOutlinedIcon fontSize="small" />} label={entityLabelPlural(type)} path={`/records/${type}`} onNavigate={onNavigate} />
        ))}
        {!isLoading && entityTypes.length === 0 && (
          <ListItemText secondary="No published forms yet" sx={{ px: 3 }} />
        )}
        <SectionLabel>Settings</SectionLabel>
        {isAdmin && <NavItem icon={<DynamicFormOutlinedIcon fontSize="small" />} label="Forms" path="/forms" onNavigate={onNavigate} />}
        {isAdmin && <NavItem icon={<BoltOutlinedIcon fontSize="small" />} label="Automations" path="/settings/automations" onNavigate={onNavigate} />}
        {isAdmin && <NavItem icon={<CampaignOutlinedIcon fontSize="small" />} label="Campaigns" path="/campaigns" onNavigate={onNavigate} />}
        {isAdmin && <NavItem icon={<AccountTreeOutlinedIcon fontSize="small" />} label="Process" path="/settings/process" onNavigate={onNavigate} />}
        {isAdmin && <NavItem icon={<WhatsAppIcon fontSize="small" />} label="WhatsApp" path="/settings/whatsapp" onNavigate={onNavigate} />}
        {isAdmin && <NavItem icon={<ViewQuiltOutlinedIcon fontSize="small" />} label="Templates" path="/settings/templates" onNavigate={onNavigate} />}
        <NavItem icon={<GroupOutlinedIcon fontSize="small" />} label="Members" path="/settings/members" onNavigate={onNavigate} />
        {isAdmin && <NavItem icon={<AdminPanelSettingsOutlinedIcon fontSize="small" />} label="Roles" path="/settings/roles" onNavigate={onNavigate} />}
        {isAdmin && <NavItem icon={<HubOutlinedIcon fontSize="small" />} label="Integrations" path="/settings/integrations" onNavigate={onNavigate} />}
        {isAdmin && <NavItem icon={<DraftsOutlinedIcon fontSize="small" />} label="Email templates" path="/settings/email-templates" onNavigate={onNavigate} />}
        {isAdmin && <NavItem icon={<DescriptionOutlinedIcon fontSize="small" />} label="Document templates" path="/settings/document-templates" onNavigate={onNavigate} />}
        <NavItem icon={<ReplyAllOutlinedIcon fontSize="small" />} label="Email & calendar" path="/settings/email-replies" onNavigate={onNavigate} />
        {isAdmin && <NavItem icon={<PrivacyTipOutlinedIcon fontSize="small" />} label="Privacy & data" path="/settings/privacy" onNavigate={onNavigate} />}
        {isAdmin && subscription?.billingEnabled && <NavItem icon={<CreditCardOutlinedIcon fontSize="small" />} label="Billing" path="/settings/billing" onNavigate={onNavigate} />}
        {isPlatformAdmin && (
          <>
            <SectionLabel>Platform</SectionLabel>
            <NavItem icon={<ApartmentOutlinedIcon fontSize="small" />} label="Tenants" path="/platform/tenants" onNavigate={onNavigate} />
            <NavItem icon={<SellOutlinedIcon fontSize="small" />} label="Plans & billing" path="/platform/billing" onNavigate={onNavigate} />
          </>
        )}
      </List>
    </Box>
  )
}

const Sidebar = ({ mobileOpen, onClose }) => (
  <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
    >
      <Toolbar variant="dense" />
      <SidebarContent onNavigate={onClose} />
    </Drawer>
    <Drawer
      variant="permanent"
      open
      sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
    >
      <Toolbar variant="dense" />
      <SidebarContent />
    </Drawer>
  </Box>
)

export default Sidebar
